import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { validateCode } from '../../api/chat-api';

// Styled components
const GradientContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(to right, #89CFF0 40.33% 50%, white 50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 24,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  padding: theme.spacing(3),
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& img': {
    height: 100,
    width: 'auto',
    maxWidth: '100%',
    objectFit: 'contain',
  },
}));

const CodeInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const CodeTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    width: 64,
    height: 64,
    '& input': {
      textAlign: 'center',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      padding: 0,
      textTransform: 'uppercase',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: 12,
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #27548a 100%)',
  borderRadius: 12,
  padding: '16px 32px',
  fontSize: '1.125rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1a3f6b 100%)',
    boxShadow: '0 12px 32px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: 'none',
    transform: 'none',
  },
  transition: 'all 0.2s ease-in-out',
}));

const AccessCodeScreen = () => {
  const [code, setCode] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const isHexChar = (ch) => /^[A-F0-9]$/.test(ch);

  const handleInputChange = (index, raw) => {
    const upper = (raw || '').toUpperCase();

    // only take the first hex char if valid
    const nextChar = upper.slice(0, 1);
    if (nextChar && !isHexChar(nextChar)) return;

    const newCode = [...code];
    newCode[index] = nextChar;
    setCode(newCode);
    setError('');

    if (nextChar && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    } else if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').toUpperCase();
    const hexOnly = (pasted.match(/[A-F0-9]/g) || []).slice(0, 4);

    if (hexOnly.length) {
      const filled = Array(4).fill('');
      for (let i = 0; i < hexOnly.length; i++) filled[i] = hexOnly[i];
      setCode(filled);
      if (hexOnly.length < 4) {
        inputRefs.current[hexOnly.length]?.focus();
      } else {
        inputRefs.current[3]?.focus();
      }
      setError('');
    }
  };

  const handleSubmit = async () => {
    const fullCode = code.join('').toUpperCase();

    if (fullCode.length !== 4 || !/^[A-F0-9]{4}$/.test(fullCode)) {
      setError('Please enter a 4-character code (0–9, A–F).');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await validateCode({
        code: fullCode,
        date: new Date().toISOString(),
      });

      if (response.data?.success && response.data?.data?.redirectUrl) {
        window.location.href = response.data.data.redirectUrl;
      } else {
        setError('Invalid access code. Please try again.');
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || 'Invalid access code. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isCodeComplete = code.every((c) => c !== '');

  return (
    <GradientContainer>
      <Container maxWidth="sm">
        <StyledCard elevation={8}>
          <CardContent>
            <Box mb={4}>
              <LogoContainer>
                <img src="/logo.png" alt="Puzzle Panda" />
              </LogoContainer>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ lineHeight: 1.5 }}
              >
                Enter your 4-character access code to begin your adventure
              </Typography>
            </Box>

            <Box mb={4}>
              <CodeInputContainer>
                {code.map((digit, index) => (
                  <CodeTextField
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    variant="outlined"
                    inputProps={{
                      inputMode: 'text',
                      pattern: '[A-Fa-f0-9]*',
                      maxLength: 1,
                      autoCorrect: 'off',
                      autoCapitalize: 'characters',
                      spellCheck: 'false',
                    }}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    color={digit ? 'primary' : 'inherit'}
                  />
                ))}
              </CodeInputContainer>

              <GradientButton
                fullWidth
                size="large"
                onClick={handleSubmit}
                disabled={!isCodeComplete || loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Validating...' : 'Start Your Adventure'}
              </GradientButton>
            </Box>

            {error && (
              <Box mb={3}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              </Box>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                🎯 Your access code was provided with your booking confirmation.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Need help? Contact our team for assistance.
              </Typography>
            </Box>
          </CardContent>
        </StyledCard>
      </Container>
    </GradientContainer>
  );
};

export default AccessCodeScreen;